export interface Index {
  schema_version: number;
  generated_at?: string;
  prefabs: Prefab[];
  assets: Asset[];
}

export interface Prefab {
  id: string;
  version: string;
  kind?: "asset" | "library";
  category?: string;
  license?: string;
  tags?: string[];
  description: string;
  thumbnail: string;
  features?: string[];
  asset_count: number;
}

export interface Asset {
  prefab: string;
  version: string;
  id: string;
  kind: string;
  category?: string;
  tags?: string[];
  description: string;
  thumbnail?: string;
  media_url?: string;
  frame_count?: number;
  width?: number;
  height?: number;
}

/** Prefab `kind` is omitted in JSON when it is the default "asset". */
export function prefabKind(p: Prefab): "asset" | "library" {
  return p.kind === "library" ? "library" : "asset";
}

export function isLibrary(p: Prefab): boolean {
  return prefabKind(p) === "library";
}

/**
 * Compare two version strings semver-style: split on ".", compare segments
 * numerically when both are integers, else lexically. Returns <0 if a<b, >0 if
 * a>b, 0 if equal. Gives `0.1.10` > `0.1.2` and stays stable for non-semver.
 */
export function compareVersions(a: string, b: string): number {
  const pa = a.split(".");
  const pb = b.split(".");
  const n = Math.max(pa.length, pb.length);
  for (let i = 0; i < n; i++) {
    const sa = pa[i] ?? "";
    const sb = pb[i] ?? "";
    const na = Number(sa);
    const nb = Number(sb);
    const numeric =
      sa !== "" && sb !== "" && Number.isInteger(na) && Number.isInteger(nb);
    if (numeric) {
      if (na !== nb) return na - nb;
    } else {
      const c = sa.localeCompare(sb);
      if (c !== 0) return c;
    }
  }
  return 0;
}

/** Highest version in a non-empty list, by `compareVersions`. */
export function latestVersion(versions: string[]): string {
  return versions.reduce((best, v) => (compareVersions(v, best) > 0 ? v : best));
}

/** A prefab id with its revisions, newest first, and the latest entry. */
export interface PrefabGroup {
  id: string;
  latest: Prefab;
  revisions: Prefab[];
}

/**
 * Group prefab entries by id, preserving first-seen order. Each group's
 * `revisions` are sorted newest-first and `latest` is the highest version.
 */
export function groupPrefabs(prefabs: Prefab[]): PrefabGroup[] {
  const order: string[] = [];
  const byId = new Map<string, Prefab[]>();
  for (const p of prefabs) {
    let entries = byId.get(p.id);
    if (!entries) {
      entries = [];
      byId.set(p.id, entries);
      order.push(p.id);
    }
    entries.push(p);
  }
  return order.map((id) => {
    const revisions = byId
      .get(id)!
      .slice()
      .sort((a, b) => compareVersions(b.version, a.version));
    return { id, latest: revisions[0], revisions };
  });
}

/** Map of prefab id → its latest version string, across all entries. */
export function latestVersionByPrefab(prefabs: Prefab[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const p of prefabs) {
    const cur = m.get(p.id);
    if (cur === undefined || compareVersions(p.version, cur) > 0) {
      m.set(p.id, p.version);
    }
  }
  return m;
}
